import { FreebeManualImportAction } from './FreebeManualImportAction';
import { FreebeBankBalanceFallback } from './FreebeBankBalanceFallback';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import '../styles/modules-freebe-integration.css';
import { FreebeImportScheduleCard } from './FreebeImportScheduleCard';
import { FreebeMfaDialog } from './FreebeMfaDialog';
import { FreebeDeclarationsCard } from './FreebeDeclarationsCard';
import { VatDeclarationsCard } from './VatDeclarationsCard';
import { vatDeclarationsApi } from './vat-declarations-api';
import type { VatImportReceipt } from '@tosho/contracts';
import { PrototypeIcon } from './PrototypeIcon';
import { SupplierConsolidationPanel } from './SupplierConsolidationPanel';
import { financialOverviewApi } from './financial-overview-api';
import {
  assertManualImportCompleted,
  manualImportErrorMessage,
  FreebeIntegrationApiError,
  freebeIntegrationApi,
  type FreebeConnectionStatus,
  type FreebeImportRunResult,
  type FreebeDeclarationImportReceipt,
  type FreebeIntegrationResult,
} from './freebe-integration-api';

interface FreebeIntegrationPanelProps {
  readonly csrfToken: string;
}

const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeStyle: 'short',
});
const VERIFYING_NOTICE = 'Vérification de la session Freebe en cours…';
const MAXIMUM_MANUAL_EXPORT_BYTES = 15_728_640;

const freebeSectionNavigation = [
  { label: 'Importations', targetId: 'freebe-imports-title' },
  { label: 'Déclarations URSSAF', targetId: 'freebe-declarations-section' },
  { label: 'Déclarations TVA', targetId: 'vat-declarations-section' },
  { label: 'Connexion à Freebe', targetId: 'freebe-connection-section' },
  { label: 'Planification', targetId: 'freebe-schedule-title' },
  { label: 'Historique', targetId: 'freebe-history-title' },
  {
    label: 'Consolidation fournisseurs',
    targetId: 'supplier-consolidation-title',
  },
] as const;

export function FreebeIntegrationPanel({
  csrfToken,
}: FreebeIntegrationPanelProps): React.JSX.Element {
  const [result, setResult] = useState<FreebeIntegrationResult | null>(null);
  const [mfa, setMfa] = useState<
    Awaited<ReturnType<typeof freebeIntegrationApi.pendingMfa>>[number] | null
  >(null);
  const [mfaDismissed, setMfaDismissed] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    const poll = (): void => {
      void freebeIntegrationApi
        .pendingMfa()
        .then((items) => {
          const first = items[0];
          if (active && Array.isArray(items)) setMfa(first ?? null);
        })
        .catch(() => undefined);
    };
    poll();
    const timer = window.setInterval(poll, 2000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);
  const [vatHistory, setVatHistory] = useState<readonly VatImportReceipt[]>([]);
  useEffect(() => {
    let active = true;
    void vatDeclarationsApi.history().then(
      (rows) => {
        if (active) setVatHistory(rows);
      },
      () => {
        if (active) setError('L’historique TVA ne peut pas être chargé.');
      },
    );
    return () => {
      active = false;
    };
  }, []);
  const [consolidatedSupplierCount, setConsolidatedSupplierCount] = useState<number | null>(null);
  const [accountEmail, setAccountEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState<
    | 'save'
    | 'disable'
    | 'launch-all'
    | 'launch-clients'
    | 'launch-contacts'
    | 'launch-suppliers'
    | 'launch-purchases'
    | 'launch-sales'
    | 'launch-quotes'
    | 'launch-balance'
    | 'launch-operations'
    | 'launch-movements'
    | 'upload-clients'
    | 'upload-contacts'
    | 'upload-suppliers'
    | 'upload-purchases'
    | 'upload-sales'
    | 'recalculate-sales'
    | 'upload-quotes'
    | 'upload-operations'
    | 'upload-movements'
    | 'authorize'
    | 'verify'
    | 'cancel'
    | null
  >(null);
  const [manualFile, setManualFile] = useState<File | null>(null);
  const [contactManualFile, setContactManualFile] = useState<File | null>(null);
  const [supplierManualFile, setSupplierManualFile] = useState<File | null>(null);
  const [purchaseManualFile, setPurchaseManualFile] = useState<File | null>(null);
  const [salesManualFile, setSalesManualFile] = useState<File | null>(null);
  const [quoteManualFile, setQuoteManualFile] = useState<File | null>(null);
  const [operationManualFile, setOperationManualFile] = useState<File | null>(null);
  const [movementManualFile, setMovementManualFile] = useState<File | null>(null);
  const clientFileInputRef = useRef<HTMLInputElement>(null);
  const contactFileInputRef = useRef<HTMLInputElement>(null);
  const supplierFileInputRef = useRef<HTMLInputElement>(null);
  const purchaseFileInputRef = useRef<HTMLInputElement>(null);
  const salesFileInputRef = useRef<HTMLInputElement>(null);
  const quoteFileInputRef = useRef<HTMLInputElement>(null);
  const operationFileInputRef = useRef<HTMLInputElement>(null);
  const movementFileInputRef = useRef<HTMLInputElement>(null);
  const [historyLimit, setHistoryLimit] = useState(5);
  const [declarationHistory, setDeclarationHistory] = useState<
    readonly FreebeDeclarationImportReceipt[]
  >([]);
  const [cancellingRunId, setCancellingRunId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    const next = await freebeIntegrationApi.read();
    setResult(next);
    setAccountEmail(next.connection?.accountEmail ?? '');
  }

  useEffect(() => {
    let active = true;
    void freebeIntegrationApi
      .read()
      .then((next) => {
        if (!active) return;
        setResult(next);
        setAccountEmail(next.connection?.accountEmail ?? '');
      })
      .catch(() => {
        if (active) setError("L'intégration Freebe ne peut pas être chargée.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    void freebeIntegrationApi
      .readDeclarationHistory()
      .then(setDeclarationHistory)
      .catch(() => {
        setError('L’historique des déclarations URSSAF ne peut pas être chargé.');
      });
  }, []);

  const authorizationStatus = result?.authorization?.status ?? null;
  useEffect(() => {
    if (
      authorizationStatus !== 'QUEUED' &&
      authorizationStatus !== 'STARTING_BROWSER' &&
      authorizationStatus !== 'AWAITING_USER' &&
      authorizationStatus !== 'VERIFYING'
    ) {
      return;
    }
    const timer = window.setInterval(() => {
      void refresh().catch(() => undefined);
    }, 2_000);
    return () => window.clearInterval(timer);
  }, [authorizationStatus]);

  const hasImportInProgress =
    result?.history.some((run) => isImportInProgress(run.status)) ?? false;
  useEffect(() => {
    if (!hasImportInProgress) return;
    const timer = window.setInterval(() => {
      void refresh().catch(() => undefined);
    }, 2_000);
    return () => window.clearInterval(timer);
  }, [hasImportInProgress]);

  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setPendingAction('save');
    setError(null);
    setNotice(null);
    try {
      await freebeIntegrationApi.saveDirectLoginProfile(accountEmail, csrfToken);
      await refresh();
      setNotice('Compte Freebe enregistré. La connexion à Freebe reste à finaliser.');
    } catch {
      setError('Le compte Freebe n’a pas pu être enregistré. Vérifiez l’adresse indiquée.');
    } finally {
      setPendingAction(null);
    }
  }

  async function disable(): Promise<void> {
    setPendingAction('disable');
    setError(null);
    setNotice(null);
    try {
      await freebeIntegrationApi.disable(csrfToken);
      await refresh();
      setNotice('La connexion Freebe est désactivée. Son historique reste conservé.');
    } catch {
      setError('La connexion Freebe n’a pas pu être désactivée.');
    } finally {
      setPendingAction(null);
    }
  }

  async function launchNow(
    family:
      | 'CLIENTS'
      | 'CONTACTS'
      | 'SUPPLIERS'
      | 'PURCHASES'
      | 'SALES'
      | 'QUOTES'
      | 'BALANCE'
      | 'OPERATIONS'
      | 'MOVEMENTS'
      | 'ALL',
  ): Promise<void> {
    const action =
      family === 'ALL'
        ? 'launch-all'
        : family === 'BALANCE'
          ? 'launch-balance'
          : family === 'MOVEMENTS'
            ? 'launch-movements'
            : family === 'OPERATIONS'
              ? 'launch-operations'
              : family === 'SALES'
                ? 'launch-sales'
                : family === 'QUOTES'
                  ? 'launch-quotes'
                  : family === 'PURCHASES'
                    ? 'launch-purchases'
                    : family === 'SUPPLIERS'
                      ? 'launch-suppliers'
                      : family === 'CONTACTS'
                        ? 'launch-contacts'
                        : 'launch-clients';
    setPendingAction(action);
    setError(null);
    setNotice(null);
    try {
      await freebeIntegrationApi.launchNow(family, csrfToken);
      await refresh();
      setNotice(
        family === 'ALL'
          ? 'Synchronisation Clients, Contacts, Fournisseurs, Factures d’achat, Factures de vente, Devis, Solde bancaire, Opérations puis Mouvements ajoutée à la file de traitement.'
          : `Mise à jour ${familyLabel(family)} ajoutée à la file de traitement.`,
      );
    } catch (caught) {
      setError(
        caught instanceof FreebeIntegrationApiError &&
          caught.code === 'FREEBE_AUTHORIZATION_REQUIRED'
          ? 'Finalisez d’abord la connexion au compte Freebe.'
          : 'L’import ne peut pas être lancé pour le moment.',
      );
    } finally {
      setPendingAction(null);
    }
  }

  async function uploadClientsManual(): Promise<void> {
    if (manualFile === null) return;
    if (!isValidJsonExport(manualFile)) {
      setNotice(null);
      setError('Sélectionnez un export Clients JSON de 15 Mio maximum.');
      return;
    }
    setPendingAction('upload-clients');
    setError(null);
    setNotice(null);
    try {
      const imported = await freebeIntegrationApi.uploadClientsJson(manualFile, csrfToken);
      assertManualImportCompleted(imported);
      setManualFile(null);
      if (clientFileInputRef.current !== null) clientFileInputRef.current.value = '';
      await refresh().catch(() => undefined);
      setNotice('Fichier Clients contrôlé et importé dans la bibliothèque Freebe.');
    } catch (caught) {
      setError(manualImportErrorMessage(caught));
      await refresh().catch(() => undefined);
      throw caught;
    } finally {
      setPendingAction(null);
    }
  }

  async function uploadContactsManual(): Promise<void> {
    if (contactManualFile === null) return;
    if (!isValidJsonExport(contactManualFile)) {
      setNotice(null);
      setError('Sélectionnez un export Contacts JSON de 15 Mio maximum.');
      return;
    }
    setPendingAction('upload-contacts');
    setError(null);
    setNotice(null);
    try {
      const imported = await freebeIntegrationApi.uploadContactsJson(contactManualFile, csrfToken);
      assertManualImportCompleted(imported);
      setContactManualFile(null);
      if (contactFileInputRef.current !== null) contactFileInputRef.current.value = '';
      await refresh().catch(() => undefined);
      setNotice('Fichier Contacts contrôlé et importé dans la bibliothèque Freebe.');
    } catch (caught) {
      setError(manualImportErrorMessage(caught));
      await refresh().catch(() => undefined);
      throw caught;
    } finally {
      setPendingAction(null);
    }
  }

  async function uploadSuppliersManual(): Promise<void> {
    if (supplierManualFile === null) return;
    if (!isValidJsonExport(supplierManualFile)) {
      setNotice(null);
      setError('Sélectionnez un export Fournisseurs JSON de 15 Mio maximum.');
      return;
    }
    setPendingAction('upload-suppliers');
    setError(null);
    setNotice(null);
    try {
      const imported = await freebeIntegrationApi.uploadSuppliersJson(
        supplierManualFile,
        csrfToken,
      );
      assertManualImportCompleted(imported);
      setSupplierManualFile(null);
      if (supplierFileInputRef.current !== null) supplierFileInputRef.current.value = '';
      await refresh().catch(() => undefined);
      setNotice('Fichier Fournisseurs contrôlé et importé dans la bibliothèque Freebe.');
    } catch (caught) {
      setError(manualImportErrorMessage(caught));
      await refresh().catch(() => undefined);
      throw caught;
    } finally {
      setPendingAction(null);
    }
  }
  async function uploadPurchasesManual(): Promise<void> {
    if (purchaseManualFile === null) return;
    if (!isValidJsonExport(purchaseManualFile)) {
      setNotice(null);
      setError('Sélectionnez un export Factures d’achat JSON de 15 Mio maximum.');
      return;
    }
    setPendingAction('upload-purchases');
    setError(null);
    setNotice(null);
    try {
      const imported = await freebeIntegrationApi.uploadPurchasesJson(
        purchaseManualFile,
        csrfToken,
      );
      assertManualImportCompleted(imported);
      setPurchaseManualFile(null);
      if (purchaseFileInputRef.current !== null) purchaseFileInputRef.current.value = '';
      await refresh().catch(() => undefined);
      setNotice('Fichier Factures d’achat contrôlé et importé dans la bibliothèque Freebe.');
    } catch (caught) {
      setError(manualImportErrorMessage(caught));
      await refresh().catch(() => undefined);
      throw caught;
    } finally {
      setPendingAction(null);
    }
  }

  async function uploadSalesManual(): Promise<void> {
    if (salesManualFile === null) return;
    if (!isValidJsonExport(salesManualFile)) {
      setNotice(null);
      setError('Sélectionnez un export Factures de vente JSON de 15 Mio maximum.');
      return;
    }
    setPendingAction('upload-sales');
    setError(null);
    setNotice(null);
    try {
      const imported = await freebeIntegrationApi.uploadSalesInvoicesJson(
        salesManualFile,
        csrfToken,
      );
      assertManualImportCompleted(imported);
      setSalesManualFile(null);
      if (salesFileInputRef.current !== null) salesFileInputRef.current.value = '';
      await refresh().catch(() => undefined);
      setNotice('Fichier Factures de vente contrôlé et importé dans la bibliothèque Freebe.');
    } catch (caught) {
      setError(manualImportErrorMessage(caught));
      await refresh().catch(() => undefined);
      throw caught;
    } finally {
      setPendingAction(null);
    }
  }

  async function recalculateSales(): Promise<void> {
    if (
      busy ||
      salesManualFile === null ||
      !isValidJsonExport(salesManualFile) ||
      connection === null ||
      connection.status === 'DISABLED'
    )
      return;
    setPendingAction('recalculate-sales');
    setNotice(null);
    setError(null);
    try {
      const result = await financialOverviewApi.reprojectSales(salesManualFile, csrfToken);
      setNotice(
        `${result.matchedCount} pièces identiques reconnues, ${result.createdCount} bases de calcul actualisées. ${result.staleCount} pièces différentes et ${result.missingCount} pièces absentes non reprises. Les factures et leur pointage restent intacts.`,
      );
      setSalesManualFile(null);
      if (salesFileInputRef.current) salesFileInputRef.current.value = '';
    } catch {
      setError(
        'Les calculs ne peuvent pas être actualisés depuis ce fichier. Sélectionnez un export JSON de factures de vente déjà importées.',
      );
    } finally {
      setPendingAction(null);
    }
  }

  async function uploadQuotesManual(): Promise<void> {
    if (quoteManualFile === null) return;
    if (!isValidXlsxExport(quoteManualFile)) {
      setNotice(null);
      setError('Sélectionnez un export Devis XLSX de 15 Mio maximum.');
      return;
    }
    setPendingAction('upload-quotes');
    setError(null);
    setNotice(null);
    try {
      const imported = await freebeIntegrationApi.uploadQuotesXlsx(quoteManualFile, csrfToken);
      assertManualImportCompleted(imported);
      setQuoteManualFile(null);
      if (quoteFileInputRef.current !== null) quoteFileInputRef.current.value = '';
      await refresh().catch(() => undefined);
      setNotice('Fichier Devis XLSX contrôlé et importé dans la bibliothèque Freebe.');
    } catch (caught) {
      setError(manualImportErrorMessage(caught));
      await refresh().catch(() => undefined);
      throw caught;
    } finally {
      setPendingAction(null);
    }
  }

  async function uploadOperationsManual(): Promise<void> {
    if (operationManualFile === null) return;
    if (!isValidJsonExport(operationManualFile)) {
      setNotice(null);
      setError('Sélectionnez un export Opérations JSON de 15 Mio maximum.');
      return;
    }
    setPendingAction('upload-operations');
    setError(null);
    setNotice(null);
    try {
      const imported = await freebeIntegrationApi.uploadOperationsJson(
        operationManualFile,
        csrfToken,
      );
      assertManualImportCompleted(imported);
      setOperationManualFile(null);
      if (operationFileInputRef.current !== null) operationFileInputRef.current.value = '';
      await refresh().catch(() => undefined);
      setNotice('Fichier Opérations JSON contrôlé et importé dans la bibliothèque Freebe.');
    } catch (caught) {
      setError(manualImportErrorMessage(caught));
      await refresh().catch(() => undefined);
      throw caught;
    } finally {
      setPendingAction(null);
    }
  }

  async function uploadMovementsManual(): Promise<void> {
    if (movementManualFile === null) return;
    if (!isValidXlsxExport(movementManualFile)) {
      setNotice(null);
      setError('Sélectionnez un export Mouvements XLSX de 15 Mio maximum.');
      return;
    }
    setPendingAction('upload-movements');
    setError(null);
    setNotice(null);
    try {
      const imported = await freebeIntegrationApi.uploadMovementsXlsx(
        movementManualFile,
        csrfToken,
      );
      assertManualImportCompleted(imported);
      setMovementManualFile(null);
      if (movementFileInputRef.current !== null) movementFileInputRef.current.value = '';
      await refresh().catch(() => undefined);
      setNotice('Fichier Mouvements XLSX contrôlé et importé dans la bibliothèque Freebe.');
    } catch (caught) {
      setError(manualImportErrorMessage(caught));
      await refresh().catch(() => undefined);
      throw caught;
    } finally {
      setPendingAction(null);
    }
  }

  async function beginAuthorization(): Promise<void> {
    setPendingAction('authorize');
    setError(null);
    setNotice(null);
    try {
      await freebeIntegrationApi.beginAuthorization(csrfToken);
      await refresh();
      setNotice('Le navigateur sécurisé démarre. Il sera disponible pendant dix minutes.');
    } catch {
      setError('Une autre connexion est déjà en cours ou le navigateur est indisponible.');
    } finally {
      setPendingAction(null);
    }
  }

  async function verifyAuthorization(): Promise<void> {
    const attempt = result?.authorization;
    if (attempt === null || attempt === undefined) return;
    setPendingAction('verify');
    setError(null);
    setNotice(null);
    try {
      await freebeIntegrationApi.verifyAuthorization(attempt.id, csrfToken);
      await refresh();
      setNotice(VERIFYING_NOTICE);
    } catch {
      setError('La session ne peut pas encore être vérifiée.');
    } finally {
      setPendingAction(null);
    }
  }

  async function cancelAuthorization(): Promise<void> {
    const attempt = result?.authorization;
    if (attempt === null || attempt === undefined) return;
    setPendingAction('cancel');
    setError(null);
    try {
      await freebeIntegrationApi.cancelAuthorization(attempt.id, csrfToken);
      await refresh();
      setNotice('Connexion annulée. Le navigateur temporaire est fermé.');
    } catch {
      setError('La connexion ne peut pas être annulée.');
    } finally {
      setPendingAction(null);
    }
  }

  async function cancelImport(run: FreebeImportRunResult): Promise<void> {
    if (!isImportInProgress(run.status)) return;
    const confirmed = window.confirm(
      'Arrêter cet import ? Les données déjà validées resteront intactes et aucune nouvelle donnée ne sera appliquée.',
    );
    if (!confirmed) return;
    setCancellingRunId(run.id);
    setError(null);
    setNotice(null);
    try {
      await freebeIntegrationApi.cancelImport(run.id, csrfToken);
      await refresh();
      setNotice('Import arrêté. La tentative reste visible dans l’historique.');
    } catch {
      setError('Cet import est déjà terminé ou ne peut plus être arrêté.');
      await refresh().catch(() => undefined);
    } finally {
      setCancellingRunId(null);
    }
  }

  const connection = result?.connection ?? null;
  const canLaunch = connection?.status === 'ACTIVE' && connection.authorizedAt !== null;
  const busy = pendingAction !== null || cancellingRunId !== null;
  const authorization = result?.authorization ?? null;
  const authorizationInProgress =
    authorization?.status === 'QUEUED' ||
    authorization?.status === 'STARTING_BROWSER' ||
    authorization?.status === 'AWAITING_USER' ||
    authorization?.status === 'VERIFYING';
  const displayedNotice =
    notice === VERIFYING_NOTICE && authorization?.status !== 'VERIFYING' ? null : notice;
  const history: readonly (
    | { kind: 'run'; value: FreebeImportRunResult }
    | { kind: 'declaration' | 'vat'; value: FreebeDeclarationImportReceipt }
  )[] = [
    ...(result?.history ?? []).map((value) => ({ kind: 'run' as const, value })),
    ...declarationHistory.map((value) => ({ kind: 'declaration' as const, value })),
    ...vatHistory.map((value) => ({ kind: 'vat' as const, value })),
  ].sort(
    (a, b) =>
      new Date(b.kind === 'run' ? b.value.startedAt : b.value.completedAt).getTime() -
      new Date(a.kind === 'run' ? a.value.startedAt : a.value.completedAt).getTime(),
  );
  const visibleHistory = history.slice(0, historyLimit);
  const clientSummary = datasetSummary(result, 'CLIENTS');
  const contactSummary = datasetSummary(result, 'CONTACTS');
  const supplierSummary = datasetSummary(result, 'SUPPLIERS');
  const purchaseSummary = datasetSummary(result, 'PURCHASES');
  const salesSummary = datasetSummary(result, 'SALES');
  const quoteSummary = datasetSummary(result, 'QUOTES');
  const balanceSummary = datasetSummary(result, 'BALANCE');
  const operationSummary = datasetSummary(result, 'OPERATIONS');
  const movementSummary = datasetSummary(result, 'MOVEMENTS');

  return (
    <div className="app-content freebe-integration">
      {mfa && mfa.id !== mfaDismissed ? (
        <FreebeMfaDialog
          key={mfa.id}
          challenge={mfa}
          csrfToken={csrfToken}
          onClose={() => setMfaDismissed(mfa.id)}
        />
      ) : null}
      {mfa && mfa.id === mfaDismissed ? (
        <button type="button" onClick={() => setMfaDismissed(null)}>
          Freebe attend votre code MFA — ouvrir la saisie
        </button>
      ) : null}
      <header className="freebe-integration__heading">
        <div>
          <p className="dashboard-heading__eyebrow">Paramètres · Intégrations</p>
          <h1>Freebe</h1>
        </div>
        <div className="freebe-integration__global-launch">
          <button
            className="freebe-integration__launch"
            type="button"
            disabled={!canLaunch || busy}
            onClick={() => void launchNow('ALL')}
          >
            <PrototypeIcon name="history" />
            {pendingAction === 'launch-all' ? 'Synchronisation…' : 'Synchro de MAJ globale'}
          </button>
        </div>
      </header>

      <nav className="freebe-integration__section-nav" aria-label="Navigation dans la page">
        {freebeSectionNavigation.map((item) => (
          <button
            type="button"
            key={item.targetId}
            onClick={() =>
              document
                .getElementById(item.targetId)
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          >
            {item.label}
          </button>
        ))}
      </nav>

      {error ? (
        <p className="freebe-integration__message tosho-feedback is-error" role="alert">
          {error}
        </p>
      ) : null}
      {displayedNotice ? (
        <p className="freebe-integration__message tosho-feedback is-success" role="status">
          {displayedNotice}
        </p>
      ) : null}

      <section className="freebe-integration__datasets" aria-labelledby="freebe-imports-title">
        <header>
          <div>
            <p className="dashboard-panel__eyebrow">Données Freebe</p>
            <h2 id="freebe-imports-title">Importations</h2>
          </div>
        </header>

        <div className="freebe-integration__dataset-grid" role="list">
          <article className="freebe-integration__dataset is-available" role="listitem">
            <span className="freebe-integration__dataset-icon" aria-hidden="true">
              <PrototypeIcon name="users-round" />
            </span>
            <span className="freebe-integration__dataset-copy">
              <strong>Clients</strong>
              <small>Bibliothèque de référence et pré-liste Tosho</small>
              <small className="freebe-integration__dataset-count">
                {datasetCountLabel(clientSummary, 'client')}
              </small>
              <small className="freebe-integration__dataset-last-update">
                {datasetLastUpdateLabel(clientSummary)}
              </small>
            </span>
            <span className="freebe-integration__dataset-state is-available">Opérationnel</span>
            <span className="freebe-integration__dataset-actions">
              <input
                ref={clientFileInputRef}
                id="freebe-clients-file"
                type="file"
                accept=".json,application/json"
                hidden
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onChange={(event) => setManualFile(event.currentTarget.files?.[0] ?? null)}
              />
              <button
                type="button"
                className="is-secondary"
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onClick={() => clientFileInputRef.current?.click()}
              >
                Parcourir
              </button>
              {manualFile !== null ? (
                <span className="freebe-integration__selected-file">
                  <span title={manualFile.name}>{manualFile.name}</span>
                  <FreebeManualImportAction
                    file={manualFile}
                    family="clients"
                    csrfToken={csrfToken}
                    disabled={busy}
                    onImport={uploadClientsManual}
                  />
                </span>
              ) : null}
              <button
                type="button"
                disabled={!canLaunch || busy}
                onClick={() => void launchNow('CLIENTS')}
              >
                {pendingAction === 'launch-clients' ? 'Mise à jour…' : 'Lancer l’import de MAJ'}
              </button>
            </span>
          </article>

          <article className="freebe-integration__dataset is-available" role="listitem">
            <span className="freebe-integration__dataset-icon" aria-hidden="true">
              <PrototypeIcon name="users-round" />
            </span>
            <span className="freebe-integration__dataset-copy">
              <strong>Contacts</strong>
              <small>Personnes rattachées aux Clients ou Fournisseurs Freebe</small>
              <small className="freebe-integration__dataset-count">
                {datasetCountLabel(contactSummary, 'contact')}
              </small>
              <small className="freebe-integration__dataset-last-update">
                {datasetLastUpdateLabel(contactSummary)}
              </small>
            </span>
            <span className="freebe-integration__dataset-state is-available">Opérationnel</span>
            <span className="freebe-integration__dataset-actions">
              <input
                ref={contactFileInputRef}
                id="freebe-contacts-file"
                type="file"
                accept=".json,application/json"
                hidden
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onChange={(event) => setContactManualFile(event.currentTarget.files?.[0] ?? null)}
              />
              <button
                type="button"
                className="is-secondary"
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onClick={() => contactFileInputRef.current?.click()}
              >
                Parcourir
              </button>
              {contactManualFile !== null ? (
                <span className="freebe-integration__selected-file">
                  <span title={contactManualFile.name}>{contactManualFile.name}</span>
                  <FreebeManualImportAction
                    file={contactManualFile}
                    family="contacts"
                    csrfToken={csrfToken}
                    disabled={busy}
                    onImport={uploadContactsManual}
                  />
                </span>
              ) : null}
              <button
                type="button"
                disabled={!canLaunch || busy}
                onClick={() => void launchNow('CONTACTS')}
              >
                {pendingAction === 'launch-contacts' ? 'Mise à jour…' : 'Lancer l’import de MAJ'}
              </button>
            </span>
          </article>

          <article className="freebe-integration__dataset is-available" role="listitem">
            <span className="freebe-integration__dataset-icon" aria-hidden="true">
              <PrototypeIcon name="users-round" />
            </span>
            <span className="freebe-integration__dataset-copy">
              <strong>Fournisseurs</strong>
              <small>Tiers liés aux achats, débours et analyses de coûts</small>
              <small className="freebe-integration__dataset-count">
                {consolidatedSupplierCount === null
                  ? datasetCountLabel(supplierSummary, 'fournisseur')
                  : consolidatedSupplierCountLabel(consolidatedSupplierCount)}
              </small>
              {consolidatedSupplierCount !== null &&
              consolidatedSupplierCount !== supplierSummary.elementCount ? (
                <small className="freebe-integration__dataset-count">
                  {supplierSummary.elementCount} fiches Freebe source
                </small>
              ) : null}
              <small className="freebe-integration__dataset-last-update">
                {datasetLastUpdateLabel(supplierSummary)}
              </small>
            </span>
            <span className="freebe-integration__dataset-state is-available">Opérationnel</span>
            <span className="freebe-integration__dataset-actions">
              <input
                ref={supplierFileInputRef}
                id="freebe-suppliers-file"
                type="file"
                accept=".json,application/json"
                hidden
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onChange={(event) => setSupplierManualFile(event.currentTarget.files?.[0] ?? null)}
              />
              <button
                type="button"
                className="is-secondary"
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onClick={() => supplierFileInputRef.current?.click()}
              >
                Parcourir
              </button>
              {supplierManualFile !== null ? (
                <span className="freebe-integration__selected-file">
                  <span title={supplierManualFile.name}>{supplierManualFile.name}</span>
                  <FreebeManualImportAction
                    file={supplierManualFile}
                    family="suppliers"
                    csrfToken={csrfToken}
                    disabled={busy}
                    onImport={uploadSuppliersManual}
                  />
                </span>
              ) : null}
              <button
                type="button"
                disabled={!canLaunch || busy}
                onClick={() => void launchNow('SUPPLIERS')}
              >
                {pendingAction === 'launch-suppliers' ? 'Mise à jour…' : 'Lancer l’import de MAJ'}
              </button>
            </span>
          </article>

          <article className="freebe-integration__dataset is-available" role="listitem">
            <span className="freebe-integration__dataset-icon" aria-hidden="true">
              <PrototypeIcon name="file-text" />
            </span>
            <span className="freebe-integration__dataset-copy">
              <strong>Factures d’achat</strong>
              <small>Achats, justificatifs et débours reliés aux fournisseurs source</small>
              <small className="freebe-integration__dataset-count">
                {datasetCountLabel(purchaseSummary, 'facture d’achat')}
              </small>
              <small className="freebe-integration__dataset-last-update">
                {datasetLastUpdateLabel(purchaseSummary)}
              </small>
            </span>
            <span className="freebe-integration__dataset-state is-available">Opérationnel</span>
            <span className="freebe-integration__dataset-actions">
              <input
                ref={purchaseFileInputRef}
                id="freebe-purchases-file"
                type="file"
                accept=".json,application/json"
                hidden
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onChange={(event) => setPurchaseManualFile(event.currentTarget.files?.[0] ?? null)}
              />
              <button
                type="button"
                className="is-secondary"
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onClick={() => purchaseFileInputRef.current?.click()}
              >
                Parcourir
              </button>
              {purchaseManualFile !== null ? (
                <span className="freebe-integration__selected-file">
                  <span title={purchaseManualFile.name}>{purchaseManualFile.name}</span>
                  <FreebeManualImportAction
                    file={purchaseManualFile}
                    family="purchases"
                    csrfToken={csrfToken}
                    disabled={busy}
                    onImport={uploadPurchasesManual}
                  />
                </span>
              ) : null}
              <button
                type="button"
                disabled={!canLaunch || busy}
                onClick={() => void launchNow('PURCHASES')}
              >
                {pendingAction === 'launch-purchases' ? 'Mise à jour…' : 'Lancer l’import de MAJ'}
              </button>
            </span>
          </article>

          <article className="freebe-integration__dataset is-available" role="listitem">
            <span className="freebe-integration__dataset-icon" aria-hidden="true">
              <PrototypeIcon name="file-text" />
            </span>
            <span className="freebe-integration__dataset-copy">
              <strong>Factures de vente</strong>
              <small>Factures, avoirs, acomptes et soldes reliés aux clients source</small>
              <small className="freebe-integration__dataset-count">
                {datasetCountLabel(salesSummary, 'facture de vente')}
              </small>
              <small className="freebe-integration__dataset-last-update">
                {datasetLastUpdateLabel(salesSummary)}
              </small>
            </span>
            <span className="freebe-integration__dataset-state is-available">Opérationnel</span>
            <span className="freebe-integration__dataset-actions">
              <input
                ref={salesFileInputRef}
                id="freebe-sales-file"
                type="file"
                accept=".json,application/json"
                hidden
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onChange={(event) => setSalesManualFile(event.currentTarget.files?.[0] ?? null)}
              />
              <button
                type="button"
                className="is-secondary"
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onClick={() => salesFileInputRef.current?.click()}
              >
                Parcourir
              </button>
              {salesManualFile !== null ? (
                <span className="freebe-integration__selected-file">
                  <span title={salesManualFile.name}>{salesManualFile.name}</span>
                  <FreebeManualImportAction
                    file={salesManualFile}
                    family="sales"
                    csrfToken={csrfToken}
                    disabled={busy}
                    onImport={uploadSalesManual}
                  />
                  <button
                    type="button"
                    className="is-secondary"
                    disabled={busy || !isValidJsonExport(salesManualFile)}
                    onClick={() => void recalculateSales()}
                  >
                    {pendingAction === 'recalculate-sales'
                      ? 'Calculs en cours…'
                      : 'Actualiser les calculs'}
                  </button>
                </span>
              ) : null}
              <button
                type="button"
                disabled={!canLaunch || busy}
                onClick={() => void launchNow('SALES')}
              >
                {pendingAction === 'launch-sales' ? 'Mise à jour…' : 'Lancer l’import de MAJ'}
              </button>
            </span>
          </article>

          <article className="freebe-integration__dataset is-available" role="listitem">
            <span className="freebe-integration__dataset-icon" aria-hidden="true">
              <PrototypeIcon name="file-text" />
            </span>
            <span className="freebe-integration__dataset-copy">
              <strong>Devis</strong>
              <small>Documents commerciaux et statuts issus des exports XLSX Freebe</small>
              <small className="freebe-integration__dataset-count">
                {datasetCountLabel(quoteSummary, 'devis')}
              </small>
              <small className="freebe-integration__dataset-last-update">
                {datasetLastUpdateLabel(quoteSummary)}
              </small>
            </span>
            <span className="freebe-integration__dataset-state is-available">Opérationnel</span>
            <span className="freebe-integration__dataset-actions">
              <input
                ref={quoteFileInputRef}
                id="freebe-quotes-file"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hidden
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onChange={(event) => setQuoteManualFile(event.currentTarget.files?.[0] ?? null)}
              />
              <button
                type="button"
                className="is-secondary"
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onClick={() => quoteFileInputRef.current?.click()}
              >
                Parcourir
              </button>
              {quoteManualFile !== null ? (
                <span className="freebe-integration__selected-file">
                  <span title={quoteManualFile.name}>{quoteManualFile.name}</span>
                  <FreebeManualImportAction
                    file={quoteManualFile}
                    family="quotes"
                    csrfToken={csrfToken}
                    disabled={busy}
                    onImport={uploadQuotesManual}
                  />
                </span>
              ) : null}
              <button
                type="button"
                disabled={!canLaunch || busy}
                onClick={() => void launchNow('QUOTES')}
              >
                {pendingAction === 'launch-quotes' ? 'Mise à jour…' : 'Lancer l’import de MAJ'}
              </button>
            </span>
          </article>

          <article className="freebe-integration__dataset is-available" role="listitem">
            <span className="freebe-integration__dataset-icon" aria-hidden="true">
              <PrototypeIcon name="coins" />
            </span>
            <span className="freebe-integration__dataset-copy">
              <strong>Solde bancaire Freebe</strong>
              <small>
                Consultation des comptes bancaires synchronisés dans Freebe, sans fichier à importer
              </small>
              <small className="freebe-integration__dataset-count">
                {datasetCountLabel(balanceSummary, 'compte bancaire')}
              </small>
              <small className="freebe-integration__dataset-last-update">
                {datasetLastUpdateLabel(balanceSummary)}
              </small>
              <small>
                {balanceSummary.sourceSynchronizedAt
                  ? `Dernière synchronisation bancaire Freebe : ${formatDate(balanceSummary.sourceSynchronizedAt)}`
                  : 'Date de synchronisation Freebe non encore consultée'}
              </small>
            </span>
            <span className="freebe-integration__dataset-state is-available">Lecture seule</span>
            <span className="freebe-integration__dataset-actions">
              <button
                type="button"
                disabled={!canLaunch || busy}
                onClick={() => void launchNow('BALANCE')}
              >
                {pendingAction === 'launch-balance' ? 'Mise à jour…' : 'Lancer la MAJ du solde'}
              </button>
              <FreebeBankBalanceFallback
                csrfToken={csrfToken}
                disabled={busy}
                synchronizedAt={balanceSummary.lastSuccessfulAt}
              />
            </span>
          </article>

          <article className="freebe-integration__dataset is-available" role="listitem">
            <span className="freebe-integration__dataset-icon" aria-hidden="true">
              <PrototypeIcon name="coins" />
            </span>
            <span className="freebe-integration__dataset-copy">
              <strong>Opérations</strong>
              <small>Encaissements et décaissements datés issus de l’export JSON Freebe</small>
              <small className="freebe-integration__dataset-count">
                {datasetCountLabel(operationSummary, 'opération')}
              </small>
              <small className="freebe-integration__dataset-last-update">
                {datasetLastUpdateLabel(operationSummary)}
              </small>
            </span>
            <span className="freebe-integration__dataset-state is-available">Opérationnel</span>
            <span className="freebe-integration__dataset-actions">
              <input
                ref={operationFileInputRef}
                id="freebe-operations-file"
                type="file"
                accept=".json,application/json"
                hidden
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onChange={(event) => setOperationManualFile(event.currentTarget.files?.[0] ?? null)}
              />
              <button
                type="button"
                className="is-secondary"
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onClick={() => operationFileInputRef.current?.click()}
              >
                Parcourir
              </button>
              {operationManualFile !== null ? (
                <span className="freebe-integration__selected-file">
                  <span title={operationManualFile.name}>{operationManualFile.name}</span>
                  <FreebeManualImportAction
                    file={operationManualFile}
                    family="operations"
                    csrfToken={csrfToken}
                    disabled={busy}
                    onImport={uploadOperationsManual}
                  />
                </span>
              ) : null}
              <button
                type="button"
                disabled={!canLaunch || busy}
                onClick={() => void launchNow('OPERATIONS')}
              >
                {pendingAction === 'launch-operations' ? 'Mise à jour…' : 'Lancer l’import de MAJ'}
              </button>
            </span>
          </article>

          <article className="freebe-integration__dataset is-available" role="listitem">
            <span className="freebe-integration__dataset-icon" aria-hidden="true">
              <PrototypeIcon name="coins" />
            </span>
            <span className="freebe-integration__dataset-copy">
              <strong>Mouvements</strong>
              <small>Mouvements bancaires et états de rapprochement issus de l’export XLSX</small>
              <small className="freebe-integration__dataset-count">
                {datasetCountLabel(movementSummary, 'mouvement')}
              </small>
              <small className="freebe-integration__dataset-last-update">
                {datasetLastUpdateLabel(movementSummary)}
              </small>
            </span>
            <span className="freebe-integration__dataset-state is-available">Opérationnel</span>
            <span className="freebe-integration__dataset-actions">
              <input
                ref={movementFileInputRef}
                id="freebe-movements-file"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hidden
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onChange={(event) => setMovementManualFile(event.currentTarget.files?.[0] ?? null)}
              />
              <button
                type="button"
                className="is-secondary"
                disabled={busy || connection === null || connection.status === 'DISABLED'}
                onClick={() => movementFileInputRef.current?.click()}
              >
                Parcourir
              </button>
              {movementManualFile !== null ? (
                <span className="freebe-integration__selected-file">
                  <span title={movementManualFile.name}>{movementManualFile.name}</span>
                  <FreebeManualImportAction
                    file={movementManualFile}
                    family="movements"
                    csrfToken={csrfToken}
                    disabled={busy}
                    onImport={uploadMovementsManual}
                  />
                </span>
              ) : null}
              <button
                type="button"
                disabled={!canLaunch || busy}
                onClick={() => void launchNow('MOVEMENTS')}
              >
                {pendingAction === 'launch-movements' ? 'Mise à jour…' : 'Lancer l’import de MAJ'}
              </button>
            </span>
          </article>
        </div>
        <FreebeDeclarationsCard
          csrfToken={csrfToken}
          enabled={!busy && connection !== null && connection.status !== 'DISABLED'}
          onImported={() =>
            void freebeIntegrationApi
              .readDeclarationHistory()
              .then(setDeclarationHistory)
              .catch(() =>
                setError('L’historique des déclarations URSSAF ne peut pas être actualisé.'),
              )
          }
        />
        <VatDeclarationsCard
          csrfToken={csrfToken}
          onImported={() =>
            void vatDeclarationsApi
              .history()
              .then(setVatHistory)
              .catch(() => setError('L’historique TVA ne peut pas être actualisé.'))
          }
        />
      </section>

      <section
        id="freebe-connection-section"
        className="freebe-integration__grid"
        aria-label="Connexion à Freebe"
        aria-busy={loading}
      >
        <article className="freebe-integration__card">
          <header>
            <p className="dashboard-panel__eyebrow">Connexion source</p>
            <h2>Compte Freebe</h2>
          </header>
          <form onSubmit={(event) => void save(event)}>
            <label className="freebe-integration__field">
              <span>Adresse e-mail du compte Freebe</span>
              <input
                type="email"
                autoComplete="email"
                required
                maxLength={320}
                value={accountEmail}
                onChange={(event) => setAccountEmail(event.currentTarget.value)}
                placeholder="prenom@entreprise.fr"
                disabled={loading || busy || authorizationInProgress}
              />
            </label>
            <div className="freebe-integration__security-note">
              <PrototypeIcon name="shield-check" />
              <p>
                <strong>Le mot de passe Freebe n’est jamais demandé ni stocké par Tosho.</strong>
                <span>
                  Vous le saisirez uniquement sur la page Freebe du navigateur temporaire.
                </span>
              </p>
            </div>
            <div className="freebe-integration__actions">
              <button
                type="submit"
                disabled={
                  loading || busy || authorizationInProgress || accountEmail.trim().length === 0
                }
              >
                {pendingAction === 'save' ? 'Enregistrement…' : 'Enregistrer le compte'}
              </button>
              {connection !== null && connection.status !== 'DISABLED' ? (
                <button
                  className="is-secondary"
                  type="button"
                  disabled={busy || authorizationInProgress}
                  onClick={() => void disable()}
                >
                  {pendingAction === 'disable' ? 'Désactivation…' : 'Désactiver'}
                </button>
              ) : null}
            </div>
          </form>
        </article>

        <article className="freebe-integration__card freebe-integration__status-card">
          <header>
            <p className="dashboard-panel__eyebrow">État</p>
            <h2>Connexion</h2>
          </header>
          {loading ? (
            <p>Chargement…</p>
          ) : connection === null ? (
            <p>Aucun compte Freebe n’est encore configuré.</p>
          ) : (
            <>
              <span className={`freebe-integration__status is-${connection.status.toLowerCase()}`}>
                {connectionStatusLabel(connection.status)}
              </span>
              <dl>
                <div>
                  <dt>Mode</dt>
                  <dd>E-mail et mot de passe Freebe</dd>
                </div>
                <div>
                  <dt>Compte</dt>
                  <dd>{connection.accountEmail ?? 'Non renseigné'}</dd>
                </div>
                <div>
                  <dt>Dernière vérification réussie</dt>
                  <dd>
                    {connection.authorizedAt === null
                      ? 'Aucune vérification réussie'
                      : formatDate(connection.authorizedAt)}
                  </dd>
                </div>
              </dl>
              {canLaunch ? (
                <p className="freebe-integration__pending-copy">
                  La session est contrôlée en direct au lancement de chaque import. Cet état indique
                  la dernière vérification réussie, pas une connexion permanente à Freebe.
                </p>
              ) : null}
              {!canLaunch ? (
                <p className="freebe-integration__pending-copy">
                  L’import automatique restera indisponible tant que la connexion Freebe n’aura pas
                  été finalisée dans le navigateur sécurisé.
                </p>
              ) : null}
              {connection.status !== 'DISABLED' ? (
                <div className="freebe-integration__authorization-actions">
                  {!authorizationInProgress ? (
                    <button type="button" disabled={busy} onClick={() => void beginAuthorization()}>
                      {pendingAction === 'authorize'
                        ? 'Démarrage…'
                        : connection.status === 'PENDING_AUTHORIZATION'
                          ? 'Se connecter à Freebe'
                          : 'Reconnecter Freebe'}
                    </button>
                  ) : null}
                  {authorization?.status === 'AWAITING_USER' ? (
                    <>
                      <a
                        className="freebe-integration__browser-link"
                        href="/freebe-browser/?autoconnect=1&resize=scale&path=/freebe-browser/websockify"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Ouvrir le navigateur sécurisé
                      </a>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void verifyAuthorization()}
                      >
                        {pendingAction === 'verify' ? 'Vérification…' : 'J’ai terminé la connexion'}
                      </button>
                    </>
                  ) : null}
                  {authorizationInProgress ? (
                    <button
                      className="is-secondary"
                      type="button"
                      disabled={busy}
                      onClick={() => void cancelAuthorization()}
                    >
                      {pendingAction === 'cancel' ? 'Annulation…' : 'Annuler'}
                    </button>
                  ) : null}
                </div>
              ) : null}
              {authorization !== null ? (
                <p className="freebe-integration__pending-copy freebe-integration__authorization-help">
                  {authorizationLabel(authorization, connection.status)}
                </p>
              ) : null}
            </>
          )}
        </article>
      </section>

      <FreebeImportScheduleCard
        key={connection?.updatedAt ?? 'freebe-schedule'}
        csrfToken={csrfToken}
        onScheduledRunDue={refresh}
      />

      <section className="freebe-integration__history" aria-labelledby="freebe-history-title">
        <header>
          <div>
            <p className="dashboard-panel__eyebrow">Traçabilité</p>
            <h2 id="freebe-history-title">Historique des imports Freebe</h2>
          </div>
          <span>
            {history.length === 0
              ? 'Dernières tentatives'
              : `${visibleHistory.length} affichée(s) sur ${history.length}`}
          </span>
        </header>
        {loading ? (
          <p className="freebe-integration__empty">Chargement de l’historique…</p>
        ) : history.length === 0 ? (
          <p className="freebe-integration__empty">
            Aucun import n’a encore été enregistré pour cette organisation.
          </p>
        ) : (
          <div className="freebe-integration__table-wrap freebe-integration__history-desktop">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Famille</th>
                  <th>Origine</th>
                  <th>État</th>
                  <th>Rapport</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleHistory.map((entry) =>
                  entry.kind !== 'run' ? (
                    <DeclarationHistoryRow
                      key={`${entry.kind}-${entry.value.id}`}
                      receipt={entry.value}
                      vat={entry.kind === 'vat'}
                    />
                  ) : (
                    <HistoryRow
                      key={entry.value.id}
                      run={entry.value}
                      cancelling={cancellingRunId === entry.value.id}
                      actionsDisabled={busy}
                      onCancel={cancelImport}
                    />
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
        {!loading && visibleHistory.length > 0 ? (
          <div className="freebe-integration__history-mobile">
            {visibleHistory.map((entry) =>
              entry.kind !== 'run' ? (
                <details
                  className="freebe-integration__history-entry"
                  key={`${entry.kind}-${entry.value.id}`}
                >
                  <summary>
                    <span className="freebe-integration__history-entry-copy">
                      <strong>{formatDate(entry.value.completedAt)}</strong>
                      <small>
                        {entry.kind === 'vat'
                          ? 'Déclarations TVA · PDF manuels · Importé'
                          : 'Déclarations URSSAF · ZIP manuel · Importé'}
                      </small>
                    </span>
                  </summary>
                  <div className="freebe-integration__history-entry-details">
                    <p>{declarationReceiptLabel(entry.value)}</p>
                  </div>
                </details>
              ) : (
                <MobileHistoryEntry
                  key={entry.value.id}
                  run={entry.value}
                  cancelling={cancellingRunId === entry.value.id}
                  actionsDisabled={busy}
                  onCancel={cancelImport}
                />
              ),
            )}
          </div>
        ) : null}
        {!loading && history.length > 5 ? (
          <div className="freebe-integration__history-controls">
            {historyLimit < history.length ? (
              <button
                type="button"
                onClick={() => setHistoryLimit((current) => Math.min(current + 5, history.length))}
              >
                Afficher 5 de plus
              </button>
            ) : null}
            {historyLimit > 5 ? (
              <button className="is-secondary" type="button" onClick={() => setHistoryLimit(5)}>
                Réduire à 5
              </button>
            ) : null}
          </div>
        ) : null}
      </section>

      <SupplierConsolidationPanel
        csrfToken={csrfToken}
        onSupplierCountChange={setConsolidatedSupplierCount}
      />
    </div>
  );
}

function declarationReceiptLabel(receipt: FreebeDeclarationImportReceipt): string {
  const scope =
    receipt.mode === 'current_year'
      ? 'Année courante'
      : receipt.mode === 'historical'
        ? 'Historique'
        : 'Archive antérieure';
  return `${scope} · ${receipt.imported} ajoutée(s), ${receipt.unchanged} déjà connue(s) · PDF officiels utilisés automatiquement pour les calculs`;
}

function DeclarationHistoryRow({
  receipt,
  vat = false,
}: {
  readonly receipt: FreebeDeclarationImportReceipt;
  readonly vat?: boolean;
}): React.JSX.Element {
  return (
    <tr>
      <td data-label="Date">{formatDate(receipt.completedAt)}</td>
      <td data-label="Famille">{vat ? 'Déclarations TVA' : 'Déclarations URSSAF'}</td>
      <td data-label="Origine">{vat ? 'PDF manuels · DGFiP' : 'ZIP manuel'}</td>
      <td data-label="État">Importé</td>
      <td data-label="Rapport">{declarationReceiptLabel(receipt)}</td>
      <td data-label="Action">
        <span className="freebe-integration__no-action" aria-hidden="true">
          —
        </span>
      </td>
    </tr>
  );
}

function HistoryRow({
  run,
  cancelling,
  actionsDisabled,
  onCancel,
}: {
  readonly run: FreebeImportRunResult;
  readonly cancelling: boolean;
  readonly actionsDisabled: boolean;
  readonly onCancel: (run: FreebeImportRunResult) => Promise<void>;
}): React.JSX.Element {
  const inProgress = isImportInProgress(run.status);

  return (
    <tr aria-busy={inProgress || undefined}>
      <td data-label="Date">{formatDate(run.startedAt)}</td>
      <td data-label="Famille">{familyLabel(run.family)}</td>
      <td data-label="Origine">{runOriginLabel(run)}</td>
      <td data-label="État">
        <span className={inProgress ? 'freebe-integration__run-status is-processing' : undefined}>
          {inProgress ? <span className="freebe-integration__spinner" aria-hidden="true" /> : null}
          <span>{runStatusLabel(run.status)}</span>
        </span>
      </td>
      <td data-label="Rapport" aria-live={inProgress ? 'polite' : undefined}>
        {historyReportLabel(run)}
      </td>
      <td data-label="Action">
        {inProgress ? (
          <button
            className="freebe-integration__stop-import"
            type="button"
            disabled={actionsDisabled}
            onClick={() => void onCancel(run)}
          >
            {cancelling ? 'Arrêt…' : 'Arrêter'}
          </button>
        ) : (
          <span className="freebe-integration__no-action" aria-hidden="true">
            —
          </span>
        )}
      </td>
    </tr>
  );
}

function MobileHistoryEntry({
  run,
  cancelling,
  actionsDisabled,
  onCancel,
}: {
  readonly run: FreebeImportRunResult;
  readonly cancelling: boolean;
  readonly actionsDisabled: boolean;
  readonly onCancel: (run: FreebeImportRunResult) => Promise<void>;
}): React.JSX.Element {
  const inProgress = isImportInProgress(run.status);
  return (
    <details className="freebe-integration__history-entry">
      <summary>
        <span className="freebe-integration__history-entry-copy">
          <strong>{formatDate(run.startedAt)}</strong>
          <small>
            {familyLabel(run.family)} · {runOriginLabel(run)} · {runStatusLabel(run.status)}
          </small>
        </span>
      </summary>
      <div className="freebe-integration__history-entry-details">
        <p aria-live={inProgress ? 'polite' : undefined}>{historyReportLabel(run)}</p>
        {inProgress ? (
          <button
            className="freebe-integration__stop-import"
            type="button"
            disabled={actionsDisabled}
            onClick={() => void onCancel(run)}
          >
            {cancelling ? 'Arrêt…' : 'Arrêter'}
          </button>
        ) : null}
      </div>
    </details>
  );
}

function historyReportLabel(run: FreebeImportRunResult): string {
  if (run.awaitingMfa && run.status === 'ACQUIRING')
    return 'Synchronisation en pause : Freebe attend votre code MFA. Ouvrez la demande sous la cloche.';
  if (run.family === 'BALANCE' && run.status === 'COMPLETED')
    return 'Solde bancaire Freebe consulté et enregistré, sans import de fichier.';
  if (isImportInProgress(run.status)) return importProgressLabel(run.status, run.family);
  if (run.status === 'FAILED') return importFailureLabel(run.failureCode, run.family, run.preview);
  if (run.application !== null) return applicationLabel(run);
  if (run.preview !== null) return previewLabel(run.preview, run.family);
  if (run.report === null) return importFailureLabel(run.failureCode, run.family);
  return `${run.report.fileCount} fichier(s), ${run.report.errorCount} erreur(s), ${run.report.warningCount} avertissement(s)`;
}

function applicationLabel(run: FreebeImportRunResult): string {
  if (run.application === null) return 'Bilan indisponible';
  const received = run.report?.rowCount;
  const result = `${run.application.createCount} ajoutée(s), ${run.application.updateCount} actualisée(s), ${run.application.unchangedCount} inchangée(s)`;
  return received === undefined
    ? `${result} dans la bibliothèque`
    : `${received} ${familyItemLabel(run.family)} Freebe reçu(s) · Bibliothèque : ${result}`;
}

function datasetSummary(
  result: FreebeIntegrationResult | null,
  family: FreebeImportRunResult['family'],
): FreebeIntegrationResult['datasets'][number] {
  return (
    result?.datasets.find((dataset) => dataset.family === family) ?? {
      family,
      elementCount: 0,
      lastSuccessfulAt: null,
    }
  );
}

function datasetCountLabel(
  summary: FreebeIntegrationResult['datasets'][number],
  singular:
    | 'client'
    | 'contact'
    | 'fournisseur'
    | 'facture d’achat'
    | 'facture de vente'
    | 'devis'
    | 'opération'
    | 'compte bancaire'
    | 'mouvement',
): string {
  let plural = `${singular}s`;
  if (singular === 'facture d’achat') plural = 'factures d’achat';
  if (singular === 'facture de vente') plural = 'factures de vente';
  if (singular === 'devis') plural = 'devis';
  if (singular === 'compte bancaire') plural = 'comptes bancaires';
  return `${summary.elementCount} ${summary.elementCount === 1 ? singular : plural}`;
}

function consolidatedSupplierCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'fournisseur consolidé' : 'fournisseurs consolidés'}`;
}

function datasetLastUpdateLabel(summary: FreebeIntegrationResult['datasets'][number]): string {
  const lastUpdate =
    summary.lastSuccessfulAt === null ? 'Aucune mise à jour' : formatDate(summary.lastSuccessfulAt);
  return `Dernière MAJ : ${lastUpdate}`;
}

function isValidJsonExport(file: File): boolean {
  return file.name.toLowerCase().endsWith('.json') && file.size <= MAXIMUM_MANUAL_EXPORT_BYTES;
}

function isValidXlsxExport(file: File): boolean {
  return file.name.toLowerCase().endsWith('.xlsx') && file.size <= MAXIMUM_MANUAL_EXPORT_BYTES;
}

function familyLabel(family: FreebeImportRunResult['family']): string {
  switch (family) {
    case 'CLIENTS':
      return 'Clients';
    case 'CONTACTS':
      return 'Contacts';
    case 'SUPPLIERS':
      return 'Fournisseurs';
    case 'PURCHASES':
      return 'Factures d’achat';
    case 'SALES':
      return 'Factures de vente';
    case 'QUOTES':
      return 'Devis';
    case 'BALANCE':
      return 'Solde bancaire Freebe';
    case 'OPERATIONS':
      return 'Opérations';
    case 'MOVEMENTS':
      return 'Mouvements';
  }
}

function familyItemLabel(family: FreebeImportRunResult['family']): string {
  switch (family) {
    case 'CLIENTS':
      return 'client(s)';
    case 'CONTACTS':
      return 'contact(s)';
    case 'SUPPLIERS':
      return 'fournisseur(s)';
    case 'PURCHASES':
      return 'facture(s) d’achat';
    case 'SALES':
      return 'facture(s) de vente';
    case 'QUOTES':
      return 'devis';
    case 'BALANCE':
      return 'compte(s) bancaire(s)';
    case 'OPERATIONS':
      return 'opération(s)';
    case 'MOVEMENTS':
      return 'mouvement(s)';
  }
}

function isImportInProgress(status: FreebeImportRunResult['status']): boolean {
  return status === 'QUEUED' || status === 'ACQUIRING' || status === 'PREFLIGHT_CHECKED';
}

function importProgressLabel(
  status: FreebeImportRunResult['status'],
  family: FreebeImportRunResult['family'],
): string {
  switch (status) {
    case 'QUEUED':
      return 'Dans la file de traitement…';
    case 'ACQUIRING':
      return family === 'BALANCE'
        ? 'Consultation du solde bancaire Freebe…'
        : 'Téléchargement et contrôle des données…';
    case 'PREFLIGHT_CHECKED':
      return `Mise à jour de la bibliothèque ${familyLabel(family)} Freebe…`;
    default:
      return 'Traitement en cours…';
  }
}

function previewLabel(
  preview: NonNullable<FreebeImportRunResult['preview']>,
  family: FreebeImportRunResult['family'],
): string {
  if (preview.status === 'BLOCKED') {
    return `Contrôle de la bibliothèque : ${preview.errorCount} erreur(s) sur ${preview.rowCount} ligne(s)`;
  }
  if (preview.status === 'REVIEW_REQUIRED') {
    return `Simulation ${familyLabel(family)} : ${preview.warningCount} avertissement(s), ${preview.conflictCount} conflit(s)`;
  }
  return `${preview.createCount} à ajouter, ${preview.updateCount} à actualiser, ${preview.unchangedCount} inchangée(s) dans la bibliothèque`;
}

function importFailureLabel(
  failureCode: string | null,
  family: FreebeImportRunResult['family'],
  preview: FreebeImportRunResult['preview'] = null,
): string {
  if (failureCode === 'FREEBE_BANK_BALANCE_UI_CHANGED')
    return 'Le solde bancaire n’a pas pu être lu dans Freebe. Aucun solde nul n’a été enregistré.';
  if (failureCode === 'FREEBE_BANK_BALANCE_AMBIGUOUS')
    return 'Les comptes Freebe ne peuvent pas être identifiés sans ambiguïté. Aucun solde n’a été enregistré.';
  const label = familyLabel(family);
  switch (failureCode) {
    case 'FREEBE_EXPORT_INCOMPLETE': {
      if (preview !== null && preview.missingFromSourceCount > 0) {
        const knownCount = preview.rowCount + preview.missingFromSourceCount;
        return `Export incomplet : ${preview.rowCount} client(s) reçu(s) sur ${knownCount} connu(s). Import interrompu sans modification.`;
      }
      return 'Export Clients incomplet. Import interrompu sans modification.';
    }
    case 'FREEBE_EXPORT_DATE_RANGE_REJECTED':
      return 'Freebe n’a pas accepté la période historique demandée.';
    case 'FREEBE_EXPORT_FILTER_STATE_REJECTED':
      return 'Les dates sont visibles, mais Freebe ne les a pas appliquées à l’export. Import interrompu sans modification.';
    case 'FREEBE_EXPORT_UI_CHANGED':
      return 'L’écran d’export Freebe ne correspond plus au parcours attendu.';
    case 'FREEBE_SESSION_EXPIRED':
      return 'La session Freebe a expiré. Reconnectez le compte puis relancez l’import.';
    case 'FREEBE_EXPORT_NOT_DOWNLOADED':
      return `Tosho n’a pas récupéré le fichier ${label} dans le délai attendu. Cela ne signifie pas qu’il n’y a aucune nouvelle donnée. La famille suivante reste tentée lors d’une synchronisation globale.`;
    case 'FREEBE_EXPORT_EXPECTED_FILE_MISSING':
      return `Un téléchargement est apparu, mais il ne correspond pas au fichier ${label} attendu. Aucun import n’a été effectué pour cette famille.`;
    case 'FREEBE_EXPORT_TECHNICAL_FAILURE':
      return 'Le navigateur de contrôle n’a pas pu terminer l’export.';
    case 'FREEBE_IMPORT_PREFLIGHT_FAILURE':
      return `Le fichier ${label} a été téléchargé, mais son contrôle de structure a échoué sans modifier la bibliothèque.`;
    case 'FREEBE_IMPORT_SIMULATION_FAILURE':
      return `Le fichier ${label} a été contrôlé, mais la simulation métier a échoué sans modifier la bibliothèque.`;
    case 'FREEBE_IMPORT_APPLICATION_FAILURE':
      return `La simulation ${label} a réussi, mais l’écriture atomique a échoué. Aucune réussite n’a été enregistrée.`;
    case 'FREEBE_IMPORT_TECHNICAL_FAILURE':
      return `Le traitement ${label} a échoué à une étape non identifiée sans modifier la bibliothèque.`;
    case 'FREEBE_AUTHORIZATION_REQUIRED':
      return 'La session Freebe n’est plus exploitable. Reconnectez le compte.';
    case 'FREEBE_IMPORT_ALREADY_RUNNING':
      return `Une autre mise à jour ${label} était déjà en cours à cette échéance.`;
    case 'WORKER_QUEUE_UNAVAILABLE':
      return 'La file de traitement était indisponible. Aucune donnée n’a été modifiée.';
    case null:
      return 'Aucun rapport disponible.';
    default:
      return 'L’import Freebe a échoué sans modifier la bibliothèque.';
  }
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? dateFormatter.format(date) : 'Date indisponible';
}

function connectionStatusLabel(status: FreebeConnectionStatus): string {
  switch (status) {
    case 'PENDING_AUTHORIZATION':
      return 'Connexion Freebe à finaliser';
    case 'AUTHORIZATION_CAPTURED':
      return 'Session capturée · vérification en cours';
    case 'ACTIVE':
      return 'Session Freebe enregistrée';
    case 'DISABLED':
      return 'Connexion désactivée';
  }
}

function authorizationLabel(
  authorization: NonNullable<FreebeIntegrationResult['authorization']>,
  connectionStatus: FreebeConnectionStatus,
): string {
  switch (authorization.status) {
    case 'QUEUED':
    case 'STARTING_BROWSER':
      return 'Préparation du navigateur sécurisé…';
    case 'AWAITING_USER':
      return 'Saisissez vos identifiants sur la page Freebe du navigateur temporaire. Si Freebe demande le code 2FA, cochez « Ne pas demander pendant 30 jours », puis confirmez ici.';
    case 'VERIFYING':
      return 'Vérification de la session chiffrée…';
    case 'COMPLETED':
      return connectionStatus === 'ACTIVE'
        ? 'Session chiffrée et vérifiée.'
        : 'Session chiffrée. Sa restauration doit encore être validée.';
    case 'FAILED':
      return authorizationFailureLabel(authorization.failureCode);
    case 'CANCELLED':
      return 'La tentative a été annulée.';
    case 'EXPIRED':
      return 'La tentative a expiré. Vous pouvez en démarrer une nouvelle.';
  }
}

function authorizationFailureLabel(failureCode: string | null): string {
  switch (failureCode) {
    case 'FREEBE_SESSION_INVALID':
      return 'Freebe est bien connecté, mais Tosho n’a pas pu capturer toute la session. Aucun accès n’a été conservé.';
    case 'FREEBE_SESSION_EXPIRED':
      return 'La session Freebe a expiré. Recommencez la connexion dans le navigateur sécurisé.';
    case 'FREEBE_EXPORT_UI_CHANGED':
      return 'Freebe a modifié son écran d’export. L’import a été arrêté sans conserver de fichier.';
    case 'FREEBE_EXPORT_NOT_DOWNLOADED':
      return 'Tosho n’a pas récupéré le fichier Clients dans le délai attendu. Aucun fichier n’a été conservé.';
    case 'FREEBE_EXPORT_EXPECTED_FILE_MISSING':
      return 'Le téléchargement reçu ne correspond pas à la famille attendue. Aucun fichier n’a été conservé.';
    case 'FREEBE_EXPORT_MULTIPLE_FILES':
      return 'Freebe a produit plusieurs fichiers inattendus. Ils ont été supprimés sans import.';
    case 'FREEBE_EXPORT_FILENAME_INVALID':
      return 'Le fichier produit par Freebe ne correspond pas à un export Clients attendu.';
    case 'FREEBE_EXPORT_FORMAT_INVALID':
      return 'Le fichier Clients produit par Freebe n’est pas un JSON valide.';
    case 'FREEBE_EXPORT_TOO_LARGE':
      return 'L’export Freebe dépasse la taille maximale autorisée de 15 Mio.';
    case 'FREEBE_EXPORT_TECHNICAL_FAILURE':
      return 'Tosho n’a pas pu restaurer la session Freebe dans le navigateur de contrôle.';
    default:
      return 'La tentative a échoué sans activer la connexion.';
  }
}

function runOriginLabel(run: FreebeImportRunResult): string {
  const origin = triggerLabel(run.trigger);
  if (run.trigger !== 'MANUAL_UPLOAD')
    return `${origin} · ${run.family === 'BALANCE' ? 'Lecture du solde' : 'MAJ annuelle'}`;
  if (!run.sourcePeriod) return `${origin} · période non renseignée`;
  const { kind, firstDate, lastDate } = run.sourcePeriod;
  const first = firstDate?.slice(0, 4);
  const last = lastDate?.slice(0, 4);
  const years = first && last ? (first === last ? first : `${first}–${last}`) : null;
  return `${origin} · ${kind === 'CURRENT_YEAR' ? 'Fichier MAJ' : kind === 'HISTORICAL' ? 'Historique' : 'Période non déterminée'}${years ? ` ${years}` : ''}`;
}

function triggerLabel(trigger: FreebeImportRunResult['trigger']): string {
  switch (trigger) {
    case 'MANUAL_UPLOAD':
      return 'Dépôt manuel';
    case 'AUTOMATIC_SCHEDULED':
      return 'Planifié';
    case 'AUTOMATIC_ON_DEMAND':
      return 'À la demande';
  }
}

function runStatusLabel(status: FreebeImportRunResult['status']): string {
  switch (status) {
    case 'QUEUED':
      return 'En attente';
    case 'ACQUIRING':
      return 'Acquisition en cours';
    case 'PREFLIGHT_BLOCKED':
      return 'Bloqué';
    case 'PREFLIGHT_REVIEW_REQUIRED':
      return 'À vérifier';
    case 'PREFLIGHT_CHECKED':
      return 'Références contrôlées';
    case 'CANCELLED':
      return 'Annulé';
    case 'FAILED':
      return 'Échec';
    case 'COMPLETED':
      return 'Terminé';
  }
}
